import { useState } from 'react';
import * as constants from '@/constants';
import { logoutUser } from '@/redux/authSlice';
import authService from '@/services/AuthService';
import * as dbConstants from '@dao/dbConstants';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutDashboard, Settings, PanelLeft } from 'lucide-react';
import { _t } from '@/i18n/t';

import { APP_ROUTES } from '@/app/routePaths';
import { useUIState } from '@/shared/state/UIStateContext';

import SidebarProfile from './SidebarProfile';
import SidebarSection from './SidebarSection';
import SidebarLogo from './SidebarLogo';
import SidebarCompanySelector from './SidebarCompanySelector';
import SidebarItem from './SidebarItem';
import { filterMenuGroups, isSelectedPath, menuGroups, topItems } from './data';
import type { MenuItem } from './types';
import type { RootState } from './types';

const itemClasses = (selected: boolean) =>
  `group relative flex items-center h-[34px] text-nav-item transition-colors ${
    selected
      ? 'bg-nav-activeBg text-nav-text-selected font-medium'
      : 'text-nav-text hover:bg-nav-activeBg'
  }`;

const footerSettingsClasses = (selected: boolean) =>
  `group relative w-full flex items-center h-sidebar-footer-item text-body font-medium transition-colors ${
    selected
      ? 'bg-nav-activeBg text-nav-text-selected'
      : 'bg-transparent text-nav-text hover:bg-nav-activeBg'
  }`;

const Sidebar = () => {
  const auth = useSelector((state: RootState) => state.auth);
  const { sidebarCollapsed: isCollapsed, toggleSidebarCollapsed, setSidebarCollapsed } = useUIState();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>(() =>
    menuGroups.reduce<Record<string, boolean>>((acc, group) => {
      if (group.collapsible) acc[group.id] = true;
      return acc;
    }, {}),
  );

  const canView = (item: MenuItem) => !item.permission || authService.hasPermission(item.permission);
  const filteredGroups = filterMenuGroups(menuGroups, canView);

  const dashboardSelected = isSelectedPath(location.pathname, [constants.ROUTE_HOME, '/dashboard', APP_ROUTES.DASHBOARD]);
  const settingsSelected = isSelectedPath(location.pathname, [
    APP_ROUTES.SETTINGS,
    constants.ROUTE_FIRM_SETTINGS,
    constants.ROUTE_USERS,
    constants.ROUTE_USER_NEW,
    constants.ROUTE_USER_EDIT,
    constants.ROUTE_TOPICS,
    constants.ROUTE_TOPIC_NEW,
    constants.ROUTE_TOPIC_EDIT,
    constants.ROUTE_ROLES,
    constants.ROUTE_ROLE_NEW,
    constants.ROUTE_ROLE_EDIT,
  ]);
  return (
    <aside
      className={`h-screen transition-all duration-200 flex flex-col ${
        isCollapsed ? 'w-nav-collapsed' : 'w-nav-expanded'
      }`}
    >
      <div className={`h-nav-header flex items-center ${isCollapsed ? 'justify-center px-0' : 'justify-between pl-3 pr-3'}`}>
        {!isCollapsed && <SidebarLogo />}

        <button
          onClick={toggleSidebarCollapsed}
          className="h-7 w-7 bg-transparent text-nav-heading hover:bg-transparent hover:text-nav-iconHover flex items-center justify-center transition-colors"
          title={isCollapsed ? _t('sidebar.expand') : _t('sidebar.collapse')}
        >
          <PanelLeft className="size-4" />
        </button>
      </div>

      <SidebarCompanySelector
        isCollapsed={isCollapsed}
        onExpandSidebar={() => setSidebarCollapsed(false)}
      />

      <nav className="flex-1 min-h-0 overflow-y-auto scrollbar-subtle py-4">
        <Link
          to={APP_ROUTES.DASHBOARD}
          className={itemClasses(dashboardSelected)}
          title={isCollapsed ? _t('sidebar.dashboard') : undefined}
        >
          {!isCollapsed && dashboardSelected && <span className="absolute left-0 inset-y-0 my-auto h-nav-active-bar w-0.5 rounded-r-full bg-nav-activeIndicator" />}
          <div className={`flex items-center gap-3 ${isCollapsed ? 'w-full justify-center px-0' : 'px-4'}`}>
            <LayoutDashboard className="h-nav-icon w-nav-icon flex-shrink-0 text-nav-icon group-hover:text-current" />
            {!isCollapsed && <span>{_t('sidebar.dashboard')}</span>}
          </div>
        </Link>

        {topItems.filter(canView).map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            isCollapsed={isCollapsed}
            selected={isSelectedPath(location.pathname, item.relatedPaths)}
          />
        ))}

        {filteredGroups.map((group) => (
          <SidebarSection
            key={group.id}
            group={group}
            isCollapsed={isCollapsed}
            isOpen={group.collapsible ? !!sectionOpen[group.id] : true}
            onToggle={() => setSectionOpen((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
            pathname={location.pathname}
          />
        ))}
      </nav>

      <div className="shrink-0">
        <div className="h-px bg-nav-footer-border" />
        <div className="h-1.5" />
        {authService.hasPermission(dbConstants.PERMISSION_FIRM_UPDATE) && (
          <Link
            to={APP_ROUTES.SETTINGS}
            className={isCollapsed
              ? `group relative w-full flex items-center justify-center h-sidebar-footer-item text-body font-medium transition-colors ${
                  settingsSelected
                    ? 'bg-nav-activeBg text-nav-text-selected'
                    : 'bg-transparent text-nav-text hover:bg-nav-activeBg'
                }`
              : footerSettingsClasses(settingsSelected)}
            title={isCollapsed ? _t('sidebar.settings') : undefined}
          >
            {!isCollapsed && settingsSelected && <span className="absolute left-0 inset-y-0 my-auto h-nav-active-bar w-0.5 rounded-r-full bg-nav-activeIndicator" />}
            <div className={`flex items-center gap-3 ${isCollapsed ? 'w-full justify-center px-0' : 'px-4'}`}>
              <Settings className="h-nav-icon w-nav-icon flex-shrink-0 text-nav-icon group-hover:text-current" />
              {!isCollapsed && <span>{_t('sidebar.settings')}</span>}
            </div>
          </Link>
        )}

        <SidebarProfile
          auth={auth}
          isCollapsed={isCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
          onNavigateProfile={() => navigate(APP_ROUTES.PROFILE)}
          onLogout={() => {
            dispatch(logoutUser(navigate) as never);
          }}
        />
        <div className="h-2.5" />
      </div>
    </aside>
  );
};

export default Sidebar;